import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { NTA_COLORS, SCROLL_NAV_RIGHT_OFFSET } from '../constants';
import { ntaPageCss } from '../typography';
import NtaScrollNavButton from './NtaScrollNavButton';

const SCROLL_ARROW_SRC = '/nta-experience/scroll-arrow.svg';
const SCROLLBAR_WIDTH = 18;
const ARROW_HEIGHT = 17;
const SCROLL_STEP = 48;
const MIN_THUMB_HEIGHT = 40;

const ScrollWrap = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollNavBar = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 52px;
  padding-right: ${SCROLL_NAV_RIGHT_OFFSET}px;
  padding-top: 8px;
  padding-bottom: 12px;
  box-sizing: border-box;
`;

const ScrollRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  gap: ${({ $canScroll }) => ($canScroll ? '8px' : '0')};
  min-width: 0;
  ${ntaPageCss}
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0 12px 16px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;

const Scrollbar = styled.div`
  width: ${({ $canScroll }) => ($canScroll ? `${SCROLLBAR_WIDTH}px` : '0')};
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  visibility: ${({ $canScroll }) => ($canScroll ? 'visible' : 'hidden')};
  pointer-events: ${({ $canScroll }) => ($canScroll ? 'auto' : 'none')};
`;

const ArrowButton = styled.button`
  width: 100%;
  height: ${ARROW_HEIGHT}px;
  flex-shrink: 0;
  border: none;
  padding: 0;
  margin: 0;
  background: ${NTA_COLORS.scrollTrack};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  ${ntaPageCss}

  &:focus-visible {
    outline: 2px solid ${NTA_COLORS.accentBlue};
    outline-offset: -2px;
  }
`;

const Track = styled.div`
  flex: 1;
  min-height: 0;
  background: ${NTA_COLORS.scrollTrack};
  position: relative;
`;

const Thumb = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  background: ${NTA_COLORS.scrollThumb};
  min-height: ${MIN_THUMB_HEIGHT}px;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const ArrowIcon = styled.img`
  width: 6px;
  height: 6px;
  display: block;
  object-fit: contain;
  transform: ${({ $flip }) => ($flip ? 'rotate(180deg)' : 'none')};
`;

const NtaQuestionScroll = forwardRef(
  (
    { children, scrollKey, showScrollNav = false, onScrollMetricsChange },
    ref
  ) => {
    const contentRef = useRef(null);
    const trackRef = useRef(null);
    const [metrics, setMetrics] = useState({
      thumbHeight: MIN_THUMB_HEIGHT,
      thumbTop: 0,
      canScroll: false,
      atTop: true,
      atBottom: true
    });

    const updateMetrics = useCallback(() => {
      const content = contentRef.current;
      const track = trackRef.current;
      if (!content || !track) return;

      const { scrollHeight, clientHeight, scrollTop } = content;
      const trackHeight = track.clientHeight;
      const canScroll = scrollHeight > clientHeight + 1;

      if (!canScroll || trackHeight <= 0) {
        setMetrics({
          thumbHeight: trackHeight || MIN_THUMB_HEIGHT,
          thumbTop: 0,
          canScroll: false,
          atTop: true,
          atBottom: true
        });
        return;
      }

      const thumbHeight = Math.max(
        MIN_THUMB_HEIGHT,
        (clientHeight / scrollHeight) * trackHeight
      );
      const maxThumbTop = trackHeight - thumbHeight;
      const scrollRatio = scrollTop / (scrollHeight - clientHeight);

      setMetrics({
        thumbHeight,
        thumbTop: scrollRatio * maxThumbTop,
        canScroll: true,
        atTop: scrollTop <= 0,
        atBottom: scrollTop + clientHeight >= scrollHeight - 1
      });
    }, []);

    useEffect(() => {
      const content = contentRef.current;
      if (!content) return undefined;

      updateMetrics();

      const onScroll = () => updateMetrics();
      content.addEventListener('scroll', onScroll, { passive: true });

      const resizeObserver = new ResizeObserver(updateMetrics);
      resizeObserver.observe(content);

      return () => {
        content.removeEventListener('scroll', onScroll);
        resizeObserver.disconnect();
      };
    }, [updateMetrics, scrollKey]);

    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
        updateMetrics();
      }
    }, [scrollKey, updateMetrics]);

    useEffect(() => {
      onScrollMetricsChange?.(metrics);
    }, [metrics, onScrollMetricsChange]);

    const scrollByStep = (direction) => {
      const content = contentRef.current;
      if (!content) return;
      content.scrollBy({ top: direction * SCROLL_STEP, behavior: 'smooth' });
    };

    const scrollToTop = () => {
      contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const scrollToBottom = () => {
      const content = contentRef.current;
      if (!content) return;
      content.scrollTo({
        top: content.scrollHeight - content.clientHeight,
        behavior: 'smooth'
      });
      window.setTimeout(() => {
        content.scrollTop = content.scrollHeight - content.clientHeight;
        updateMetrics();
      }, 350);
    };

    useImperativeHandle(ref, () => ({
      scrollToBottom
    }));

    const handleTrackClick = (event) => {
      const content = contentRef.current;
      const track = trackRef.current;
      if (!content || !track || !metrics.canScroll) return;

      const rect = track.getBoundingClientRect();
      const clickY = event.clientY - rect.top;
      const { thumbHeight } = metrics;
      const maxThumbTop = track.clientHeight - thumbHeight;
      const targetRatio = Math.min(
        1,
        Math.max(0, (clickY - thumbHeight / 2) / maxThumbTop)
      );

      content.scrollTop =
        targetRatio * (content.scrollHeight - content.clientHeight);
    };

    const handleThumbPointerDown = (event) => {
      event.preventDefault();
      const content = contentRef.current;
      const track = trackRef.current;
      if (!content || !track || !metrics.canScroll) return;

      const startY = event.clientY;
      const startScrollTop = content.scrollTop;
      const maxScroll = content.scrollHeight - content.clientHeight;
      const maxThumbTop = track.clientHeight - metrics.thumbHeight;

      const onPointerMove = (moveEvent) => {
        const deltaY = moveEvent.clientY - startY;
        const scrollDelta =
          maxThumbTop > 0 ? (deltaY / maxThumbTop) * maxScroll : 0;
        content.scrollTop = startScrollTop + scrollDelta;
      };

      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };

    const scrollRow = (
      <ScrollRow $canScroll={metrics.canScroll}>
        <Content ref={contentRef}>{children}</Content>
        <Scrollbar
          $canScroll={metrics.canScroll}
          aria-hidden={!metrics.canScroll}
        >
          <ArrowButton
            type="button"
            aria-label="Scroll up"
            $hidden={!metrics.canScroll || metrics.atTop}
            onClick={() => scrollByStep(-1)}
          >
            <ArrowIcon src={SCROLL_ARROW_SRC} width={6} height={6} alt="" />
          </ArrowButton>
          <Track ref={trackRef} onClick={handleTrackClick}>
            {metrics.canScroll ? (
              <Thumb
                style={{
                  height: `${metrics.thumbHeight}px`,
                  top: `${metrics.thumbTop}px`
                }}
                onPointerDown={handleThumbPointerDown}
                onClick={(event) => event.stopPropagation()}
              />
            ) : null}
          </Track>
          <ArrowButton
            type="button"
            aria-label="Scroll down"
            $hidden={!metrics.canScroll || metrics.atBottom}
            onClick={() => scrollByStep(1)}
          >
            <ArrowIcon
              src={SCROLL_ARROW_SRC}
              width={6}
              height={6}
              alt=""
              style={{ transform: 'rotate(180deg)' }}
            />
          </ArrowButton>
        </Scrollbar>
      </ScrollRow>
    );

    if (!showScrollNav) {
      return scrollRow;
    }

    return (
      <ScrollWrap>
        {scrollRow}
        <ScrollNavBar>
          {metrics.canScroll && !metrics.atTop ? (
            <NtaScrollNavButton direction="up" onClick={scrollToTop} />
          ) : null}
        </ScrollNavBar>
      </ScrollWrap>
    );
  }
);

NtaQuestionScroll.displayName = 'NtaQuestionScroll';

export default NtaQuestionScroll;

NtaQuestionScroll.propTypes = {
  children: PropTypes.node.isRequired,
  scrollKey: PropTypes.string,
  showScrollNav: PropTypes.bool,
  onScrollMetricsChange: PropTypes.func
};
